import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import board from './LiveBoard.module.css'
import styles from './RealtimeBoard.module.css'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'
import { todayLocal } from '../timesheets/time'
import { useLayer } from '../../components/layers'
import { useLive } from '../../lib/live'
import { chipLabel } from '../../lib/tagLabel'
import { AGE_BUCKETS, realtimeItemName, waitingLabel, type RealtimeItem, type RealtimeResult } from './model'
import type { LiveDef } from './LiveBoard'

// Realtime Collection (§7l.7), as Crew's Receipting screen shows it: what is
// still waiting in the field for a receipt, in four bubbles by time since
// pick, and a Show map button that opens a full-page satellite map of it.
// The board is today, live; its map plays the day back, and can be moved to a
// past day, which it reads on its own (the board stays on today).
//
// Every figure is the server's (GET /api/live/realtime): which items wait,
// how long, and which bubble. Ages move on their own while nothing else does,
// so the board asks every thirty seconds as well as when cycles change.
const REFRESH_MS = 30_000
const LIST_MAX = 40

const RealtimeMap = lazy(() => import('./RealtimeMap'))

interface Props {
  def: LiveDef
  wall: boolean
  onClose?: () => void
}

export function RealtimeBoard({ def, wall, onClose }: Props) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [data, setData] = useState<RealtimeResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [mapOpen, setMapOpen] = useState(false)
  // The map's day; a past one is read separately, with its playback history.
  const [mapDay, setMapDay] = useState(() => todayLocal())
  const [pastData, setPastData] = useState<{ day: string; data: RealtimeResult } | null>(null)
  const [toast, setToast] = useState('')
  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid
  const seq = useRef(0)
  // While the map is open, the day comes too, for its playback.
  const mapOpenRef = useRef(mapOpen)
  mapOpenRef.current = mapOpen

  const load = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    const n = ++seq.current
    try {
      const q: Record<string, string> = { today: todayLocal() }
      if (mapOpenRef.current) q.history = '1'
      const r = await apiJson<RealtimeResult>(u, `/api/live/realtime?${new URLSearchParams(q)}`)
      if (n !== seq.current) return
      setData(r)
      setError(null)
    } catch (e: any) {
      if (n !== seq.current) return
      setError(e?.message || 'Could not load this board')
    }
  }, [uid])

  useEffect(() => {
    load()
    const t = setInterval(load, REFRESH_MS)
    return () => clearInterval(t)
  }, [load])
  useEffect(() => {
    if (mapOpen) load()
  }, [mapOpen, load])
  const today = todayLocal()
  const openMap = () => {
    setMapDay(today)
    setMapOpen(true)
  }
  // A past day does not change, so it is read once each time it is picked.
  useEffect(() => {
    const u = userRef.current
    if (!mapOpen || !u || mapDay >= today) return
    let live = true
    apiJson<RealtimeResult>(u, `/api/live/realtime?${new URLSearchParams({ today: mapDay, history: '1' })}`)
      .then((r) => live && setPastData({ day: mapDay, data: r }))
      .catch((e: any) => live && setToast(`Could not load ${mapDay}: ${e?.message || 'error'}`))
    return () => {
      live = false
    }
  }, [mapOpen, mapDay, today, uid])
  useLive(user, ['cycles'], (d) => d === today, load)

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3400)
    return () => clearTimeout(t)
  }, [toast])

  const closeRef = useRef(onClose)
  closeRef.current = onClose
  const boardEl = useRef<HTMLDivElement>(null)
  useLayer(!!onClose, () => closeRef.current?.(), { el: boardEl, modal: !wall })

  const popOut = () => window.open(`/live/${encodeURIComponent(def.key)}`, '_blank')
  const fullScreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen()
      else await document.documentElement.requestFullscreen()
    } catch {
      setToast('Full screen was refused by the browser')
    }
  }
  const openItem = (it: RealtimeItem) =>
    navigate(`/product-activity?${new URLSearchParams({ cycle: it.cycle_id, from: it.work_date, to: it.work_date })}`)

  const total = data?.total ?? 0
  const placed = data ? data.items.filter((i) => i.gps).length : 0

  const content = (
    <div ref={boardEl} className={`${board.board} ${wall ? board.wall : board.modal}`} role={wall ? undefined : 'dialog'} aria-label={def.name} onClick={(e) => e.stopPropagation()}>
      <div className={board.head}>
        <h1 className={board.headTitle}>{def.name}</h1>
        <span className={board.liveTag}>
          <span className={board.liveDot} />
          LIVE
        </span>
        {wall ? (
          <button type="button" className={board.headBtn} onClick={fullScreen}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M21 8V5a2 2 0 0 0-2-2h-3" /><path d="M3 16v3a2 2 0 0 0 2 2h3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" />
            </svg>
            Full screen
          </button>
        ) : (
          <>
            <button type="button" className={board.headBtn} onClick={popOut}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
              </svg>
              Open for a wallboard
            </button>
            <button type="button" className={board.closeBtn} onClick={onClose} aria-label="Close" title="Close">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden>
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </>
        )}
      </div>

      <div className={styles.ground}>
        <div className={styles.inner}>
          <div className={styles.labelRow}>
            <span className={styles.label}>Unreceipted items · today</span>
            {data && (
              <span className={total === 0 ? styles.allIn : styles.waiting} data-testid="rt-total">
                {total === 0 ? 'All received' : `${total} waiting`}
              </span>
            )}
          </div>
          {error && <div className={board.error}>{data ? `Not refreshed: ${error}. Showing the last figures.` : error}</div>}

          <div className={styles.bubbles} role="list" aria-label="Waiting by time since pick">
            {AGE_BUCKETS.map((b) => {
              const n = data?.buckets[b.k] ?? 0
              return (
                <div key={b.k} role="listitem" className={styles.bubbleCell} aria-label={`${b.label}: ${n}`}>
                  <span className={styles.bubble} style={{ background: n ? b.color : 'transparent', borderColor: b.color, color: n ? '#fff' : b.color }} data-testid={`rt-${b.k}`}>
                    {data ? n : '–'}
                    {b.warn && n > 0 && (
                      <span className={styles.warn} aria-label="Overdue" title="Overdue">
                        !
                      </span>
                    )}
                  </span>
                  <span className={styles.bubbleLabel}>{b.label}</span>
                </div>
              )
            })}
          </div>

          <button type="button" className={styles.showMap} onClick={openMap} disabled={!data}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M14.1 4.6 9.9 2.4a2 2 0 0 0-1.8 0L3.6 4.6A1 1 0 0 0 3 5.5v14a1 1 0 0 0 1.4.9l3.7-1.8a2 2 0 0 1 1.8 0l4.2 2.1a2 2 0 0 0 1.8 0l4.5-2.2a1 1 0 0 0 .6-.9v-14a1 1 0 0 0-1.4-.9l-3.7 1.8a2 2 0 0 1-1.8 0Z" />
              <path d="M15 5.8v15M9 3.2v15" />
            </svg>
            Show map
          </button>
          {data && data.off_map > 0 && (
            <div className={styles.offMap}>
              {placed === 0
                ? `${data.off_map} waiting ${data.off_map === 1 ? 'item has' : 'items have'} no GPS point, so the map has nothing to show.`
                : `${data.off_map} more with no GPS point (not on the map).`}
            </div>
          )}

          {data && data.items.length > 0 && (
            <div className={styles.list} aria-label="Waiting items, oldest first">
              {data.items.slice(0, LIST_MAX).map((it) => (
                <button key={it.cycle_id} type="button" className={styles.row} onClick={() => openItem(it)}>
                  <span className={styles.dot} style={{ background: AGE_BUCKETS.find((b) => b.k === it.bucket)?.color }} aria-hidden />
                  <span className={styles.rowMain}>
                    <span className={styles.rowItem}>{realtimeItemName(it, chipLabel)}</span>
                    <span className={styles.rowWho}>
                      {[it.workers.join(', '), it.location, it.team].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span className={styles.rowWait}>{waitingLabel(it.minutes)}</span>
                </button>
              ))}
              {data.items.length > LIST_MAX && <div className={styles.more}>and {data.items.length - LIST_MAX} more</div>}
            </div>
          )}
          {!data && !error && <div className={board.empty}>Loading…</div>}
        </div>
      </div>

      {mapOpen && data && (
        <Suspense fallback={null}>
          <RealtimeMap
            data={mapDay < today ? (pastData?.day === mapDay ? pastData.data : data) : data}
            loading={mapDay < today && pastData?.day !== mapDay}
            day={mapDay < today ? mapDay : today}
            today={today}
            onDay={setMapDay}
            user={user}
            onClose={() => setMapOpen(false)}
            onOpenItem={openItem}
          />
        </Suspense>
      )}
      {toast && <div className={board.toast} role="status">{toast}</div>}
    </div>
  )

  if (wall) return content
  return (
    <div className={board.backdrop} onClick={onClose}>
      {content}
    </div>
  )
}
